import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Payment as PaymentIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/api';

const API_URL = `${API_BASE_URL}/api`;

const RecordSchemePaymentDialog = ({ open, onClose, scheme, onSuccess }) => {
  const [formData, setFormData] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Cash',
    transactionReference: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSchedule, setPaymentSchedule] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);

  useEffect(() => {
    if (open && scheme) {
      // Pre-fill amount with monthly installment
      setFormData(prev => ({
        ...prev,
        amount: scheme.MonthlyInstallment || ''
      }));
      
      // Fetch payment schedule
      fetchPaymentSchedule();
    } else if (!open) {
      // Reset state when dialog closes
      setPaymentSchedule(null);
      setSelectedMonth(null);
      setError(null);
      setFormData({
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'Cash',
        transactionReference: '',
        notes: ''
      });
    }
  }, [open, scheme]);

  const fetchPaymentSchedule = async () => {
    if (!scheme) return;
    
    try {
      setLoadingSchedule(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/schemes/${scheme.SchemeId}/payment-schedule`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // console.log('=== PAYMENT SCHEDULE FETCHED ===');
      // console.log('Scheme ID:', scheme.SchemeId);
      // console.log('Payment Count:', response.data.paymentCount);
      // console.log('Months Data:', response.data.months);
      response.data.months.forEach(m => {
        console.log(`Month ${m.monthNumber}: status=${m.status}, paymentId=${m.paymentId}, expectedDate=${m.expectedDate}`);
      });
      console.log('================================');
      
      // Add timestamp to force re-render
      setPaymentSchedule({
        ...response.data,
        _timestamp: Date.now()
      });
      
      // Auto-select the current (next unpaid) month only on initial load
      if (!selectedMonth && !editingPayment) {
        const currentMonth = response.data.months.find(m => m.status === 'current');
        if (currentMonth) {
          console.log('Auto-selecting current month:', currentMonth.monthNumber);
          handleMonthSelect(currentMonth);
        }
      }
    } catch (err) {
      console.error('Error fetching payment schedule:', err);
      toast.error('Failed to load payment schedule');
    } finally {
      setLoadingSchedule(false);
    }
  };

  const handleMonthSelect = (month) => {
    if (month.status === 'paid' && !editingPayment) return; // Can't select paid months unless editing
    
    setSelectedMonth(month);
    setFormData(prev => ({
      ...prev,
      amount: paymentSchedule?.monthlyInstallment || scheme.MonthlyInstallment || '',
      paymentDate: month.expectedDate
    }));
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/schemes/${scheme.SchemeId}/payments/${paymentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Payment deleted successfully');
      setDeleteConfirm(null);
      
      // Clear selected month and editing state
      setSelectedMonth(null);
      setEditingPayment(null);
      
      // Reset form
      setFormData({
        amount: scheme.MonthlyInstallment || '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'Cash',
        transactionReference: '',
        notes: ''
      });
      
      // Refresh payment schedule first
      await fetchPaymentSchedule();
      
      // Call onSuccess to refresh parent data
      if (onSuccess) {
        await onSuccess();
      }
    } catch (err) {
      console.error('Error deleting payment:', err);
      toast.error('Failed to delete payment');
    }
  };

  const handleEditPayment = (month) => {
    setEditingPayment(month.paymentId);
    setSelectedMonth(month);
    setFormData({
      amount: month.amount || '',
      paymentDate: month.actualPaymentDate || month.expectedDate,
      paymentMethod: month.paymentMethod || 'Cash',
      transactionReference: month.transactionReference || '',
      notes: month.notes || ''
    });
  };

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedMonth && !editingPayment) {
      setError('Please select a month from the payment schedule');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    if (!formData.paymentDate) {
      setError('Please select a payment date');
      return;
    }

    if (!formData.paymentMethod) {
      setError('Please select a payment method');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      
      let response;
      if (editingPayment) {
        // Update existing payment
        response = await axios.put(
          `${API_URL}/schemes/${scheme.SchemeId}/payments/${editingPayment}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('Payment updated successfully');
      } else {
        // Create new payment - include monthDate from selected month
        const paymentData = {
          ...formData,
          monthDate: selectedMonth?.expectedDate
        };
        
        console.log('=== RECORDING PAYMENT ===');
        console.log('Selected Month:', selectedMonth);
        console.log('Payment Data:', paymentData);
        console.log('========================');
        
        response = await axios.post(
          `${API_URL}/schemes/${scheme.SchemeId}/payments`,
          paymentData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success(response.data.message || 'Payment recorded successfully');
        
        // Show bonus notification if triggered
        if (response.data.bonusTriggered) {
          toast.success('🎁 Bonus triggered! Scheme moved to usage phase.', {
            autoClose: 5000
          });
        }

        // Show discrepancy warning if present
        if (response.data.hasDiscrepancy) {
          toast.warning('Payment amount differs from monthly installment', {
            autoClose: 5000
          });
        }
      }

      console.log('=== PAYMENT RECORDED SUCCESSFULLY ===');
      console.log('Response:', response.data);
      console.log('====================================');
      
      // Reset form and editing state
      setFormData({
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'Cash',
        transactionReference: '',
        notes: ''
      });
      setSelectedMonth(null);
      setEditingPayment(null);
      
      // Call onSuccess to refresh parent data
      if (onSuccess) {
        console.log('Calling onSuccess to refresh parent...');
        await onSuccess();
      }
      
      // Refresh payment schedule
      console.log('Fetching updated payment schedule...');
      await fetchPaymentSchedule();
    } catch (err) {
      console.error('Error saving payment:', err);
      setError(err.response?.data?.error?.message || 'Failed to save payment');
      toast.error(err.response?.data?.error?.message || 'Failed to save payment');
    } finally {
      setLoading(false);
    }
  };

  if (!scheme) return null;

  // Use paymentSchedule data if available, otherwise fall back to scheme prop
  const actualPaymentCount = paymentSchedule?.paymentCount ?? scheme.PaymentCount ?? 0;
  const nextPaymentNumber = actualPaymentCount + 1;
  const remainingPayments = 13 - actualPaymentCount;
  const expectedAmount = parseFloat(scheme.MonthlyInstallment) || 0;
  const enteredAmount = parseFloat(formData.amount) || 0;
  const hasDiscrepancy = enteredAmount > 0 && Math.abs(expectedAmount - enteredAmount) > 0.01;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1a1a",
          border: "1px solid #333",
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: '#2b2b2b',
        borderBottom: '1px solid #333'
      }}>
        <Box>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#C59A58' }}>
            {editingPayment ? 'Edit Payment' : 'Manage Payments'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
            Scheme #{scheme.SchemeId} • {scheme.CustomerName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#C59A58' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, bgcolor: "#1a1a1a" }}>
        {/* Scheme Info */}
        <Paper sx={{ 
          p: 2, 
          mb: 3, 
          bgcolor: '#2a2a2a',
          border: '1px solid #333',
          borderRadius: 2
        }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#C59A58' }}>
                Payment Number
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
                #{nextPaymentNumber}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#C59A58' }}>
                Remaining Payments
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
                {remainingPayments}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#C59A58' }}>
                Expected Amount
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                ₹{expectedAmount.toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              bgcolor: "#2a1a1a",
              color: "#fff",
              border: "1px solid #d32f2f",
              '& .MuiAlert-icon': { color: "#d32f2f" }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {hasDiscrepancy && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              bgcolor: "#2a1a1a",
              color: "#fff",
              border: "1px solid #ff9800",
              '& .MuiAlert-icon': { color: "#ff9800" }
            }}
          >
            Payment amount differs from expected monthly installment (₹{expectedAmount.toFixed(2)})
          </Alert>
        )}

        {nextPaymentNumber === 13 && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              bgcolor: "#1a2a2a",
              color: "#fff",
              border: "1px solid #2196f3",
              '& .MuiAlert-icon': { color: "#2196f3" }
            }}
          >
            🎁 This is the final payment! Bonus will be automatically added after recording.
          </Alert>
        )}

        {/* Month Checklist */}
        {loadingSchedule ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress sx={{ color: "#C59A58" }} />
          </Box>
        ) : paymentSchedule && (
          <Paper sx={{ 
            p: 2, 
            mb: 3, 
            maxHeight: 300, 
            overflowY: 'auto',
            bgcolor: '#2a2a2a',
            border: '1px solid #333',
            borderRadius: 2
          }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#C59A58' }}>
              Select Payment Month
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {paymentSchedule.months.map((month) => (
                <Paper
                  key={`${month.monthNumber}-${paymentSchedule._timestamp}`}
                  sx={{
                    p: 1.5,
                    cursor: month.status === 'paid' ? 'default' : 'pointer',
                    bgcolor: 
                      month.status === 'paid' ? '#1a2a1a' : 
                      selectedMonth?.monthNumber === month.monthNumber ? '#1a2a3a' :
                      month.status === 'current' ? '#2a2a1a' : 
                      '#333',
                    border: selectedMonth?.monthNumber === month.monthNumber ? '2px solid #C59A58' : '1px solid #444',
                    opacity: month.status === 'paid' ? 0.8 : 1,
                    '&:hover': {
                      bgcolor: month.status === 'paid' ? '#1a2a1a' : '#444'
                    }
                  }}
                  onClick={() => handleMonthSelect(month)}
                >
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={1}>
                      {month.status === 'paid' ? (
                        <Chip 
                          icon={<PaymentIcon />} 
                          label="✓" 
                          size="small" 
                          color="success" 
                          sx={{ width: 40 }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center', color: '#fff' }}>
                          {month.monthNumber}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#fff' }}>
                        Month {month.monthNumber}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                        {new Date(month.expectedDate).toLocaleDateString('en-IN', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      {month.status === 'paid' ? (
                        <Chip label="Paid" size="small" color="success" />
                      ) : month.status === 'current' ? (
                        <Chip label="Current" size="small" color="warning" />
                      ) : (
                        <Chip label="Pending" size="small" variant="outlined" />
                      )}
                    </Grid>
                    <Grid item xs={2}>
                      {month.amount && (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                          ₹{parseFloat(month.amount).toFixed(2)}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      {month.status === 'paid' && (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Edit Payment">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditPayment(month);
                              }}
                              sx={{ color: '#C59A58' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Payment">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(month.paymentId);
                              }}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                  {month.status === 'paid' && month.actualPaymentDate && (
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#9CA3AF' }}>
                      Paid on: {new Date(month.actualPaymentDate).toLocaleDateString('en-IN')}
                      {month.paymentMethod && ` • ${month.paymentMethod}`}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          </Paper>
        )}

        {/* Payment Form */}
        {editingPayment && selectedMonth && selectedMonth.monthNumber && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              bgcolor: "#2a1a1a",
              color: "#fff",
              border: "1px solid #ff9800",
              '& .MuiAlert-icon': { color: "#ff9800" }
            }}
          >
            <strong>Editing Payment</strong> for Month {selectedMonth.monthNumber}
          </Alert>
        )}
        {selectedMonth && !editingPayment && selectedMonth.monthNumber && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              bgcolor: "#1a2a2a",
              color: "#fff",
              border: "1px solid #2196f3",
              '& .MuiAlert-icon': { color: "#2196f3" }
            }}
          >
            Recording payment for <strong>Month {selectedMonth.monthNumber}</strong> (Expected: {new Date(selectedMonth.expectedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })})
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            required
            label="Payment Amount"
            type="number"
            value={formData.amount}
            onChange={handleChange('amount')}
            slotProps={{
              input: {
                startAdornment: <Typography sx={{ mr: 1, color: "#C59A58" }}>₹</Typography>
              }
            }}
            helperText={`Expected: ₹${expectedAmount.toFixed(2)}`}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: '#C59A58' },
                '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                '& input': { color: '#fff' }
              },
              '& .MuiInputLabel-root': { 
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-focused': { color: '#C59A58' }
              },
              '& .MuiFormHelperText-root': { color: '#9CA3AF' }
            }}
          />

          <TextField
            fullWidth
            required
            label="Payment Date"
            type="date"
            value={formData.paymentDate}
            onChange={handleChange('paymentDate')}
            slotProps={{
              inputLabel: {
                shrink: true
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: '#C59A58' },
                '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                '& input': { color: '#fff' }
              },
              '& .MuiInputLabel-root': { 
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-focused': { color: '#C59A58' }
              }
            }}
          />

          <TextField
            fullWidth
            required
            select
            label="Payment Method"
            value={formData.paymentMethod}
            onChange={handleChange('paymentMethod')}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: '#C59A58' },
                '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                '& .MuiSelect-select': { color: '#fff' }
              },
              '& .MuiInputLabel-root': { 
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-focused': { color: '#C59A58' }
              }
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    bgcolor: '#2a2a2a',
                    border: '1px solid #333',
                    '& .MuiMenuItem-root': {
                      color: '#fff',
                      '&:hover': { bgcolor: '#333' },
                      '&.Mui-selected': { bgcolor: '#C59A58', color: '#000' }
                    }
                  }
                }
              }
            }}
          >
            <MenuItem value="Cash">Cash</MenuItem>
            <MenuItem value="UPI">UPI</MenuItem>
            <MenuItem value="Card">Card</MenuItem>
            <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
            <MenuItem value="Cheque">Cheque</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label="Transaction Reference"
            value={formData.transactionReference}
            onChange={handleChange('transactionReference')}
            placeholder="UPI ID, Transaction ID, Cheque Number, etc."
            helperText="Optional: Enter reference number for digital payments"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: '#C59A58' },
                '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                '& input': { color: '#fff' }
              },
              '& .MuiInputLabel-root': { 
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-focused': { color: '#C59A58' }
              },
              '& .MuiFormHelperText-root': { color: '#9CA3AF' }
            }}
          />

          <TextField
            fullWidth
            label="Notes"
            value={formData.notes}
            onChange={handleChange('notes')}
            multiline
            rows={2}
            placeholder="Any additional notes about this payment"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: '#C59A58' },
                '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                '& textarea': { color: '#fff' }
              },
              '& .MuiInputLabel-root': { 
                color: 'rgba(255,255,255,0.7)',
                '&.Mui-focused': { color: '#C59A58' }
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        bgcolor: '#2b2b2b', 
        borderTop: '1px solid #333' 
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ 
            color: '#9CA3AF',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' }
          }}
        >
          Cancel
        </Button>
        {editingPayment && (
          <Button
            onClick={() => {
              setEditingPayment(null);
              setSelectedMonth(null);
              setFormData({
                amount: scheme.MonthlyInstallment || '',
                paymentDate: new Date().toISOString().split('T')[0],
                paymentMethod: 'Cash',
                transactionReference: '',
                notes: ''
              });
            }}
            disabled={loading}
            sx={{ 
              color: '#9CA3AF',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' }
            }}
          >
            Cancel Edit
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} sx={{ color: "#000" }} /> : <PaymentIcon />}
          onClick={handleSubmit}
          disabled={loading || !formData.amount || !formData.paymentDate || (!selectedMonth && !editingPayment)}
          sx={{ bgcolor: '#C59A58', '&:hover': { bgcolor: '#b68649' }, color: '#000', fontWeight: 600 }}
        >
          {loading ? (editingPayment ? 'Updating...' : 'Recording...') : (editingPayment ? 'Update Payment' : 'Record Payment')}
        </Button>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={!!deleteConfirm} 
        onClose={() => setDeleteConfirm(null)}
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: "#C59A58" }}>Confirm Delete</DialogTitle>
        <DialogContent sx={{ bgcolor: "#1a1a1a" }}>
          <Typography sx={{ color: "#fff" }}>Are you sure you want to delete this payment? This will recalculate the scheme totals and payment numbers.</Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#1a1a1a", borderTop: "1px solid #333" }}>
          <Button 
            onClick={() => setDeleteConfirm(null)}
            sx={{ 
              color: '#9CA3AF',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              handleDeletePayment(deleteConfirm);
            }} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default RecordSchemePaymentDialog;
